import { motion } from "framer-motion";
import { useContext } from "react";
import { FaShoppingCart } from "react-icons/fa";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ADD_TO_CART_TEXT, SALE_BADGE_TEXT } from "../constants";
import Context from "../context";
import addToCart from "../helpers/addToCart";
import displayNPRCurrency from "../helpers/displayCurrency";
import scrollTop from "../helpers/scrollTop";
import WishlistButton from "./WishlistButton";

const VerticalCard = ({ loading, data = [] }) => {
  const loadingList = new Array(8).fill(null);
  const { fetchUserAddToCart } = useContext(Context);
  const user = useSelector((state) => state?.user?.user);

  const handleAddToCart = async (e, id) => {
    await addToCart(e, id);
    fetchUserAddToCart();
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
      {loading
        ? loadingList.map((_, index) => (
            <div
              key={index}
              className="bg-white rounded-card border border-surface-border overflow-hidden flex flex-col"
            >
              <div className="h-56 shimmer" />
              <div className="p-5 flex flex-col gap-3">
                <div className="h-2.5 shimmer rounded-full w-1/4" />
                <div className="h-4 shimmer rounded-full w-full" />
                <div className="h-4 shimmer rounded-full w-2/3" />
                <div className="flex items-center gap-2 pt-2">
                  <div className="h-6 shimmer rounded-full w-1/3" />
                  <div className="h-4 shimmer rounded-full w-1/4" />
                </div>
                <div className="h-10 shimmer rounded-full w-full mt-2" />
              </div>
            </div>
          ))
        : data.map((product, index) => (
            <motion.div
              key={product?._id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                delay: index * 0.05,
                duration: 0.5,
                ease: [0.16, 1, 0.3, 1],
              }}
              viewport={{ once: true }}
              className="h-full"
            >
              <div className="bg-white rounded-card border border-surface-border shadow-card hover:shadow-card-hover overflow-hidden transition-all duration-400 group flex flex-col h-full">
                {/* Image Area */}
                <Link
                  to={`/product/${product?._id}`}
                  className="relative h-56 bg-surface-muted flex items-center justify-center p-8 overflow-hidden"
                  onClick={scrollTop}
                >
                  <img
                    src={product?.productImage[0]}
                    className="h-full object-contain mix-blend-multiply transition-transform duration-600 ease-spring group-hover:scale-108"
                    alt={product?.productName}
                    style={{
                      "--tw-scale-x": "var(--scale)",
                      "--tw-scale-y": "var(--scale)",
                    }}
                  />

                  {/* Badges */}
                  {product?.price > product?.sellingPrice && (
                    <span className="badge-accent absolute top-3 left-3 shadow-sm">
                      {SALE_BADGE_TEXT}
                    </span>
                  )}

                  {/* Wishlist */}
                  <WishlistButton
                    productId={product?._id}
                    className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-sm border border-surface-border z-10 hover:bg-white transition-all"
                  />
                </Link>

                {/* Content */}
                <div className="p-5 flex flex-col flex-1">
                  <p className="tag text-neutral-400 mb-1">
                    {product?.category}
                  </p>

                  <Link
                    to={`/product/${product?._id}`}
                    onClick={scrollTop}
                    className="flex-1"
                  >
                    <h3 className="font-semibold text-base text-primary line-clamp-2 hover:text-accent transition-colors leading-snug mb-3 min-h-[2.75rem]">
                      {product?.productName}
                    </h3>
                  </Link>

                  {/* Pricing */}
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="text-xl font-black text-primary">
                      {displayNPRCurrency(product?.sellingPrice)}
                    </span>
                    {product?.price > product?.sellingPrice && (
                      <span className="text-xs text-neutral-400 line-through font-medium">
                        {displayNPRCurrency(product?.price)}
                      </span>
                    )}
                    {product?.price > product?.sellingPrice && (
                      <span className="text-xs font-black text-accent ml-auto">
                        {Math.round(
                          (1 - product.sellingPrice / product.price) * 100,
                        )}
                        % off
                      </span>
                    )}
                  </div>

                  {user?._id && (
                    <button
                      onClick={(e) => handleAddToCart(e, product?._id)}
                      className="flex items-center justify-center gap-2 h-11 bg-primary text-white text-xs font-black rounded-xl hover:bg-accent transition-all duration-300 active:scale-95 shadow-button-primary group/btn"
                    >
                      <FaShoppingCart className="text-xs group-hover/btn:scale-110 transition-transform" />
                      {ADD_TO_CART_TEXT}
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
    </div>
  );
};

export default VerticalCard;
